import { alpha, Stack, Typography } from '@mui/material';
import type {
  GridColDef,
  GridPaginationModel,
  GridSortModel,
} from '@mui/x-data-grid';
import { useRouter } from 'next/router';
import type { ReferralSummary } from '@/common/hooks/referrals/types';
import { DataGrid } from '@/components/DataGrid';
import { EmptyTableCell } from '@/components/EmptyTableCell';
import { TXM_REFERRALS_PATH } from '@/constants';
import {
  TrackReferralsEvent,
  useTrackReferralsAnalytics,
} from '$analytics/useTrackAnalytics/domains/referrals';
import { TransactionStatus } from '../../components/TransactionStatus';
import { AgreementStatus } from './AgreementStatus';
import { BothSideAgentsIndicator } from './components/BothSideAgentsIndicator';
import { ReferralTableActionsMenu } from './components/ReferralTableActionsMenu';
import { ReferralDate } from './ReferralDate';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

function isBothSideAgentsRow(row: ReferralSummary) {
  return Boolean(
    row.referringAgent?.isSideAgent && row.transactingAgent?.isSideAgent,
  );
}

const sideAgentsIndicatorColumn: GridColDef<ReferralSummary> = {
  field: 'sideAgentsIndicator',
  headerName: '',
  renderCell: ({ row }) =>
    isBothSideAgentsRow(row) ? <BothSideAgentsIndicator /> : null,
  disableColumnMenu: true,
  display: 'flex',
  align: 'right',
  sortable: false,
  width: 24,
  minWidth: 24,
  resizable: false,
};

const referringAgentColumn: GridColDef<ReferralSummary> = {
  field: 'referringAgent',
  headerName: 'Referring Agent',
  renderCell: ({ row: { referringAgent } }) => {
    if (!referringAgent) return <EmptyTableCell />;
    return (
      <Stack
        sx={{
          flexDirection: 'column',
          gap: 0,
          height: '100%',
          justifyContent: 'center',
          color: 'text.primary',
          minWidth: 0,
          '& span': {
            lineHeight: '18px',
          },
        }}
      >
        <Typography component="span" variant="subtitle2" noWrap>
          {referringAgent.displayName}
        </Typography>

        <Typography
          sx={{
            color: 'text.secondary',
          }}
          variant="body3"
          noWrap
        >
          {referringAgent.companyName}
        </Typography>
      </Stack>
    );
  },
  valueGetter: (_, row) => {
    return row.referringAgent?.displayName ?? '';
  },
  disableColumnMenu: true,
  display: 'flex',
  minWidth: 230,
  flex: 1.5,
  sortable: true,
};
const transactingAgentColumn: GridColDef<ReferralSummary> = {
  field: 'transactingAgent',
  headerName: 'Transacting Agent',
  renderCell: ({ row: { transactingAgent } }) => {
    if (!transactingAgent) return <EmptyTableCell />;
    return (
      <Stack
        sx={{
          flexDirection: 'column',
          gap: 0,
          height: '100%',
          justifyContent: 'center',
          color: 'text.primary',
          minWidth: 0,
          '& span': {
            lineHeight: '18px',
          },
        }}
      >
        <Typography component="span" variant="subtitle2" noWrap>
          {transactingAgent.displayName}
        </Typography>

        <Typography
          sx={{
            color: 'text.secondary',
          }}
          variant="body3"
          noWrap
        >
          {transactingAgent.companyName}
        </Typography>
      </Stack>
    );
  },
  valueGetter: (_, row) => {
    return row.transactingAgent?.displayName ?? '';
  },
  disableColumnMenu: true,
  display: 'flex',
  minWidth: 230,
  flex: 1.5,
  sortable: true,
};
const clientColumn: GridColDef<ReferralSummary> = {
  field: 'client',
  headerName: 'Client',
  renderCell: ({ row: { client } }) => {
    if (!client) return <EmptyTableCell />;
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
        {client.displayName}
      </Typography>
    );
  },
  disableColumnMenu: true,
  display: 'flex',
  minWidth: 140,
  flex: 1.1,
  sortable: false,
};
const clientTypeColumn: GridColDef<ReferralSummary> = {
  field: 'clientType',
  headerName: 'Client Type',
  renderCell: ({ row: { client } }) => {
    if (!client) return <EmptyTableCell />;
    return (
      <Typography
        sx={{
          color: 'text.secondary',
          textTransform: 'capitalize',
        }}
        variant="body2"
        noWrap
      >
        {client.clientTypesList.join(', ')}
      </Typography>
    );
  },
  disableColumnMenu: true,
  display: 'flex',
  flex: 1,
  minWidth: 100,
  sortable: false,
};
const agreementStatusColumn: GridColDef<ReferralSummary> = {
  field: 'agreementStatus',
  headerName: 'Agreement Status',
  renderCell: ({ row: { agreementStatus, id, transactionStatus } }) => {
    if (!agreementStatus || !transactionStatus) return <EmptyTableCell />;
    return (
      <AgreementStatus
        referralId={id}
        agreementStatus={agreementStatus}
        transactionStatus={transactionStatus}
      />
    );
  },
  disableColumnMenu: true,
  display: 'flex',
  flex: 1,
  minWidth: 150,
  sortable: false,
};
const transactionStatusColumn: GridColDef<ReferralSummary> = {
  field: 'transactionStatus',
  headerName: 'Transaction Status',
  renderCell: ({ row }) => {
    if (!row.transactionStatus) return <EmptyTableCell />;
    return <TransactionStatus referral={row} />;
  },
  disableColumnMenu: true,
  display: 'flex',
  flex: 1,
  minWidth: 150,
  sortable: false,
};
const startDateColumn: GridColDef<ReferralSummary> = {
  field: 'startDate',
  headerName: 'Start Date',
  renderCell: ({ row: { startDate } }) => {
    if (!startDate) return <EmptyTableCell />;
    return <ReferralDate date={startDate} />;
  },
  valueGetter: (_, row) => {
    return row.startDate;
  },
  disableColumnMenu: true,
  display: 'flex',
  flex: 1,
  minWidth: 120,
  sortable: true,
};
const endDateColumn: GridColDef<ReferralSummary> = {
  field: 'endDate',
  headerName: 'End Date',
  renderCell: ({ row: { endDate } }) => {
    if (!endDate) return <EmptyTableCell />;
    return <ReferralDate date={endDate} includeDistance />;
  },
  valueGetter: (_, row) => {
    return row.endDate;
  },
  disableColumnMenu: true,
  display: 'flex',
  flex: 1,
  minWidth: 140,
  sortable: true,
};

// Skeleton row type for loading state - id is required
// by MUI DataGrid for row identification
export type ReferralTableRowSkeleton = { id: string };

function getActionsColumn(isArchived: boolean): GridColDef<ReferralSummary> {
  return {
    field: 'actions',
    headerName: '',
    renderCell: ({ row }) => (
      <ReferralTableActionsMenu referralId={row.id} isArchived={isArchived} />
    ),
    disableColumnMenu: true,
    sortable: false,
    width: 50,
  };
}

type ReferralsTableProps = {
  isArchived?: boolean;
  loading: boolean;
  paginationModel: GridPaginationModel;
  rowCount: number;
  rows: ReferralSummary[] | ReferralTableRowSkeleton[];
  setPaginationModel: (paginationModel: GridPaginationModel) => void;
  setSortModel: (sortModel: GridSortModel) => void;
  sortModel: GridSortModel;
};

export function ReferralsTable(props: ReferralsTableProps) {
  const {
    isArchived,
    loading,
    paginationModel,
    rowCount,
    rows,
    setPaginationModel,
    setSortModel,
    sortModel,
  } = props;

  const router = useRouter();
  const trackEvent = useTrackReferralsAnalytics();

  return (
    <DataGrid
      columns={[
        sideAgentsIndicatorColumn,
        referringAgentColumn,
        transactingAgentColumn,
        clientColumn,
        clientTypeColumn,
        agreementStatusColumn,
        transactionStatusColumn,
        startDateColumn,
        endDateColumn,
        getActionsColumn(!!isArchived),
      ]}
      disableRowSelectionOnClick
      disableVirtualization
      initialState={{
        sorting: {
          sortModel: [{ field: 'endDate', sort: 'asc' }],
        },
      }}
      loading={loading}
      onPaginationModelChange={setPaginationModel}
      onRowClick={({ id, row }, event) => {
        if (!id) {
          return;
        }

        if (event.defaultPrevented) {
          return;
        }

        // Type guard for skeleton rows
        if (!('agreementStatus' in row)) {
          return;
        }

        const { agreementStatus, transactionStatus } = row;

        trackEvent(TrackReferralsEvent.ClickViewReferralOnListPage, {
          eventTarget: 'referral_line_item',
          interactionId: id,
          agreementStatus,
          transactionStatus,
        });

        void router.push(`${TXM_REFERRALS_PATH}/${id}`);
      }}
      onSortModelChange={setSortModel}
      sortingMode="server"
      paginationModel={paginationModel}
      paginationMode="server"
      pageSizeOptions={PAGE_SIZE_OPTIONS}
      rows={rows}
      rowCount={rowCount}
      rowHeight={68}
      sortModel={sortModel}
      getRowClassName={({ row }) =>
        'agreementStatus' in row && isBothSideAgentsRow(row)
          ? 'ReferralsTable-bothSideAgents'
          : ''
      }
      sx={{
        '& div[data-field="sideAgentsIndicator"]': {
          paddingLeft: 0,
          paddingRight: 0.5,
        },
        '& .ReferralsTable-bothSideAgents': {
          backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.04),
          '&:hover': {
            backgroundColor: (theme) =>
              alpha(theme.palette.secondary.main, 0.08),
          },
        },
      }}
    />
  );
}
